import { Info } from '@drift-labs/icons';
import {
	AMM_RESERVE_PRECISION_EXP,
	BASE_PRECISION_EXP,
	BigNum,
	PERCENTAGE_PRECISION_EXP,
	PRICE_PRECISION_EXP,
	QUOTE_PRECISION_EXP,
} from '@drift-labs/sdk';
import React, { memo, ReactNode, useCallback } from 'react';
import Text from 'src/components/Text/Text';
import useDriftActions from 'src/hooks/useDriftActions';
import { LiquidityPool } from 'src/hooks/useLiquidityPools';
import useMarketInfoDisplayData from 'src/hooks/useMarketInfoDisplayData';
import { LPOpenOrderData } from 'src/hooks/useOpenOrdersData';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useMarketsInfoStore from 'src/stores/useMarketsInfoStore';
import { marketHasInsuranceFund } from '../../hooks/useInfoForCurrentlySelectedMarket';
import Button from '../Button';
import SkeletonValuePlaceholder from '../SkeletonValuePlaceholder/SkeletonValuePlaceholder';
import Tooltip from '../Tooltip/Tooltip';
import UIHelpTextLink from '../UIHelpTextLink';
import MarketIcon from '../Utils/MarketIcon';

export const DetailsRow = (props: {
	children: ReactNode;
	fullWidth?: boolean;
}) => (
	<div
		className={`py-[10px] flex items-center flex-row justify-between min-w-[200px] items-start space-x-4 border-b border-container-border last:border-none ${
			props.fullWidth ? 'w-full' : ''
		}`}
	>
		{props.children}
	</div>
);

const SpotMarketDetails = ({
	marketIndex,
	fullWidth,
}: {
	marketIndex: number;
	fullWidth?: boolean;
}) => {
	const borrowMarketData = useDriftStore((s) => s.borrowLendData);
	const marketInfo = useMarketsInfoStore(
		(s) => s.allMarketsInfo.spotLookup[marketIndex]
	);
	const marketAccount = marketInfo?.account;
	const marketPrecisionExp = marketInfo.config.precisionExp;

	const imfFactor = marketAccount?.imfFactor;
	const displayImfFactor = imfFactor
		? BigNum.from(imfFactor, PERCENTAGE_PRECISION_EXP).prettyPrint()
		: null;

	const orderTickSize = marketAccount?.orderTickSize;
	const displayOrderTickSize = orderTickSize
		? BigNum.from(orderTickSize, PRICE_PRECISION_EXP).prettyPrint()
		: null;

	const orderStepSize = marketAccount?.orderStepSize;
	const displayOrderStepSize = BigNum.from(
		orderStepSize,
		marketPrecisionExp
	).prettyPrint();

	const minOrderSize = marketAccount?.minOrderSize;
	const displayMinOrderSize = minOrderSize
		? BigNum.from(minOrderSize, marketPrecisionExp).prettyPrint()
		: null;

	const maintLiabilityWeight = marketAccount?.maintenanceLiabilityWeight / 100;
	const mainAssetWeight = marketAccount?.maintenanceAssetWeight / 100;

	const borrowAvailability = borrowMarketData?.find(
		(market) => market.bankConfig.marketIndex === marketIndex
	)?.borrowLimitBase;

	return (
		<>
			<DetailsRow fullWidth={fullWidth}>
				<Text.BODY3>Min. Order Size</Text.BODY3>
				<Text.BODY3>{displayMinOrderSize}</Text.BODY3>
			</DetailsRow>
			<DetailsRow fullWidth={fullWidth}>
				<Text.BODY3>Order Tick Size</Text.BODY3>
				<Text.BODY3>{displayOrderTickSize}</Text.BODY3>
			</DetailsRow>
			<DetailsRow fullWidth={fullWidth}>
				<Text.BODY3>Order Step Size</Text.BODY3>
				<Text.BODY3>{displayOrderStepSize}</Text.BODY3>
			</DetailsRow>
			<DetailsRow fullWidth={fullWidth}>
				<Text.BODY3>IMF Factor</Text.BODY3>
				<Text.BODY3>{displayImfFactor}</Text.BODY3>
			</DetailsRow>
			<DetailsRow fullWidth={fullWidth}>
				<Text.BODY3>Asset Weight (Maintenance)</Text.BODY3>
				<Text.BODY3>{mainAssetWeight}%</Text.BODY3>
			</DetailsRow>
			<DetailsRow fullWidth={fullWidth}>
				<Text.BODY3>Liability Weight (Maintenance)</Text.BODY3>
				<Text.BODY3>{maintLiabilityWeight}%</Text.BODY3>
			</DetailsRow>
			<DetailsRow fullWidth={fullWidth}>
				<Text.BODY3>Available to Borrow</Text.BODY3>
				<Text.BODY3>
					{borrowAvailability?.toMillified() ?? 0} {marketInfo.config.symbol}
				</Text.BODY3>
			</DetailsRow>
		</>
	);
};

export const SpotMarketDetailsMemo = memo(SpotMarketDetails);

/**
 * Returns a list of rows with the market details for given perp market index
 */
const PerpMarketDetails = ({
	marketIndex,
	fullWidth,
}: {
	marketIndex: number;
	fullWidth?: boolean;
}) => {
	const marketInfo = useMarketsInfoStore(
		(s) => s.allMarketsInfo.perpLookup[marketIndex]
	);
	const marketAccount = marketInfo?.account;

	const imfFactor = marketAccount?.imfFactor;
	const orderTickSize = marketAccount?.amm?.orderTickSize;
	const orderStepSize = marketAccount?.amm?.orderStepSize;

	const extendedInfo = useMarketsInfoStore(
		(s) => s.allMarketsInfo.perpLookup[marketIndex]?.extendedInfo
	);

	const marketData = useMarketInfoDisplayData();

	const { minOrderSize, marginMaintenance, availableInsurance, pnlPoolValue } =
		extendedInfo || {};

	const displayMinOrderSize = minOrderSize
		? BigNum.from(minOrderSize, BASE_PRECISION_EXP).prettyPrint()
		: null;
	const displayOrderTickSize = orderTickSize
		? BigNum.from(orderTickSize, PRICE_PRECISION_EXP).prettyPrint()
		: null;
	const displayOrderStepSize = BigNum.from(
		orderStepSize,
		AMM_RESERVE_PRECISION_EXP
	).prettyPrint();

	const displayImfFactor = imfFactor
		? BigNum.from(imfFactor, PERCENTAGE_PRECISION_EXP).prettyPrint()
		: null;
	const displayMarginMaintenance = marginMaintenance / 100;
	const displayAvailableInsurance = availableInsurance
		? BigNum.from(availableInsurance, QUOTE_PRECISION_EXP).toMillified()
		: null;
	const displayPnlPoolValue = pnlPoolValue
		? BigNum.from(pnlPoolValue, QUOTE_PRECISION_EXP).toMillified()
		: null;

	const displayLiquidationFee = `${
		(marketAccount.liquidatorFee + marketAccount.ifLiquidationFee) / 10000
	}%`;

	const hasLimitedInsurance = !marketHasInsuranceFund(marketAccount);

	return (
		<>
			<DetailsRow fullWidth={fullWidth}>
				<Text.BODY3>Next Funding Update</Text.BODY3>
				<Text.BODY3>
					{marketData &&
						(marketData?.estimatedFundingRateCountdownTs <= 0 ? (
							<span>Due*</span>
						) : (
							<>
								{`${Math.floor(marketData?.estimatedFundingRateCountdownTs / 60)
									.toString()
									.padStart(2, '0')}:${(
									marketData?.estimatedFundingRateCountdownTs % 60
								)
									.toString()
									.padStart(2, '0')}`}
							</>
						))}
				</Text.BODY3>
			</DetailsRow>
			<DetailsRow fullWidth={fullWidth}>
				<Text.BODY3>Open Interest</Text.BODY3>
				<Text.BODY3>{`${marketData?.openInterest} ${marketInfo.genericInfo.baseAssetSymbol}`}</Text.BODY3>
			</DetailsRow>
			<DetailsRow fullWidth={fullWidth}>
				<Text.BODY3>Open Interest (Short)</Text.BODY3>
				<Text.BODY3>{`${marketData?.shortOpenInterest} ${marketInfo.genericInfo.baseAssetSymbol}`}</Text.BODY3>
			</DetailsRow>
			<DetailsRow fullWidth={fullWidth}>
				<Text.BODY3>Open Interest (Long)</Text.BODY3>
				<Text.BODY3>{`${marketData?.longOpenInterest} ${marketInfo.genericInfo.baseAssetSymbol}`}</Text.BODY3>
			</DetailsRow>
			<DetailsRow fullWidth={fullWidth}>
				<Text.BODY3>Max Open Interest</Text.BODY3>
				<Text.BODY3>{`${marketData?.maxOpenInterest} ${marketInfo.genericInfo.baseAssetSymbol}`}</Text.BODY3>
			</DetailsRow>
			<DetailsRow fullWidth={fullWidth}>
				<Text.BODY3>Min. Order Size</Text.BODY3>
				<Text.BODY3>{displayMinOrderSize}</Text.BODY3>
			</DetailsRow>
			<DetailsRow fullWidth={fullWidth}>
				<Text.BODY3>Order Tick Size</Text.BODY3>
				<Text.BODY3>{displayOrderTickSize}</Text.BODY3>
			</DetailsRow>
			<DetailsRow fullWidth={fullWidth}>
				<Text.BODY3>Order Step Size</Text.BODY3>
				<Text.BODY3>{displayOrderStepSize}</Text.BODY3>
			</DetailsRow>
			<DetailsRow fullWidth={fullWidth}>
				<Text.BODY3>IMF Factor</Text.BODY3>
				<Text.BODY3>{displayImfFactor}</Text.BODY3>
			</DetailsRow>
			<DetailsRow fullWidth={fullWidth}>
				<Text.BODY3>Margin Maintenance</Text.BODY3>
				<Text.BODY3>{displayMarginMaintenance}%</Text.BODY3>
			</DetailsRow>
			<DetailsRow fullWidth={fullWidth}>
				<Text.BODY3>Total Liquidation Fee</Text.BODY3>
				<Text.BODY3>{displayLiquidationFee}</Text.BODY3>
			</DetailsRow>
			<DetailsRow fullWidth={fullWidth}>
				<Text.BODY3 className="flex flex-row items-center">
					Insurance Fund
					{hasLimitedInsurance && (
						<Tooltip
							allowHover
							className="text-orange-70"
							content={
								<div className="flex flex-col space-y-2">
									<span>
										This market uses a limited insurance fund (fee pool only) to
										protect against levered losses.
									</span>{' '}
									<UIHelpTextLink
										href="https://docs.drift.trade/insurance-fund/insurance-fund-intro"
										text="Learn More"
										className="mt-2"
									/>
								</div>
							}
						>
							<Info className="ml-2" />
						</Tooltip>
					)}
				</Text.BODY3>

				<Text.BODY3>${displayAvailableInsurance}</Text.BODY3>
			</DetailsRow>
			<DetailsRow fullWidth={fullWidth}>
				<Text.BODY3>P&L Pool</Text.BODY3>
				<Text.BODY3>${displayPnlPoolValue}</Text.BODY3>
			</DetailsRow>
		</>
	);
};

export const PerpMarketDetailsMemo = memo(PerpMarketDetails);

const _ViewDetailsForMarketButton = ({
	isLiteMode,
}: {
	isLiteMode?: boolean;
}) => {
	const setState = useDriftStore((s) => s.set);
	const showMarketDetailsModal = () => {
		setState((s) => {
			s.modals.showMarketDetailsModal = true;
		});
	};

	return (
		<>
			<button
				className="flex flex-row items-center cursor-pointer"
				onClick={showMarketDetailsModal}
			>
				<div
					className={`select-none flex flex-col items-start px-2 leading-2 mt-[-3px]`}
				>
					{isLiteMode ? (
						<div className="flex text-interactive-link h-full">
							<Text.BODY2>Market Details</Text.BODY2>{' '}
						</div>
					) : (
						<>
							<div className={`text-text-label`}>
								<Text.MICRO1>About Market</Text.MICRO1>
							</div>

							<div className="flex text-interactive-link">
								<Text.BODY2>View Details</Text.BODY2>{' '}
							</div>
						</>
					)}
				</div>
			</button>
		</>
	);
};

export const ViewDetailsForMarketButton = React.memo(
	_ViewDetailsForMarketButton
);

/**
 * Returns a list of rows with the liquidity pool details for given perp market index
 */
const PoolDetails = ({
	liquidityPool,
	fullWidth,
}: {
	liquidityPool: LiquidityPool;
	fullWidth?: boolean;
}) => {
	const setState = useDriftStore((s) => s.set);

	if (!liquidityPool) return;

	const showPoolPerformance = useCallback(() => {
		setState((s) => {
			s.modals.showLpDetailsModal = false;
			s.modals.showLpPerformanceModal30d = true;
		});
	}, []);

	return (
		<div className="text-text-default">
			<DetailsRow fullWidth={fullWidth}>
				<Text.BODY3>BAL Performance</Text.BODY3>
				<Text.BODY3
					className="hover:cursor-pointer hover:text-text-label"
					onClick={showPoolPerformance}
				>
					View
				</Text.BODY3>
			</DetailsRow>
			<DetailsRow fullWidth={fullWidth}>
				<Text.BODY3>Total BAL Shares</Text.BODY3>
				<Text.BODY3>
					{liquidityPool.protocolLpShares
						.add(liquidityPool.totalUserLpShares)
						.prettyPrint(true)}
				</Text.BODY3>
			</DetailsRow>
			<DetailsRow fullWidth={fullWidth}>
				<Text.BODY3>Total Liquidity</Text.BODY3>
				<Text.BODY3>
					{liquidityPool.protocolLpQuoteValue
						.add(liquidityPool.totalUserLpQuoteValue)
						.toNotional()}
				</Text.BODY3>
			</DetailsRow>
			<DetailsRow fullWidth={fullWidth}>
				<Text.BODY3>User Provided Liquidity</Text.BODY3>
				<Text.BODY3>
					{liquidityPool.totalUserLpQuoteValue.toNotional()}
				</Text.BODY3>
			</DetailsRow>
			<DetailsRow fullWidth={fullWidth}>
				<Text.BODY3>Protocol Provided Liquidity</Text.BODY3>
				<Text.BODY3>
					{liquidityPool.protocolLpQuoteValue.toNotional()}
				</Text.BODY3>
			</DetailsRow>
			<DetailsRow fullWidth={fullWidth}>
				<Text.BODY3>24hr Trading Fees</Text.BODY3>
				{liquidityPool.totalFees24h ? (
					<Text.BODY3>{liquidityPool.totalFees24h.toNotional()}</Text.BODY3>
				) : (
					<SkeletonValuePlaceholder className="w-10 h-2" loading={true} />
				)}
			</DetailsRow>
			<DetailsRow fullWidth={fullWidth}>
				<Text.BODY3>30d Est. Fee Only APR</Text.BODY3>
				{liquidityPool.poolFeeApr ? (
					<Text.BODY3 className="text-positive-green">
						{liquidityPool.poolFeeApr.toFixed(2)}%
					</Text.BODY3>
				) : (
					<SkeletonValuePlaceholder className="w-10 h-2" loading={true} />
				)}
			</DetailsRow>
			<DetailsRow fullWidth={fullWidth}>
				<Text.BODY3>Fee Distribution</Text.BODY3>
				<Text.BODY3>80% BAL / 20% Protocol</Text.BODY3>
			</DetailsRow>
		</div>
	);
};

export const LPDetailsMemo = memo(PoolDetails);

/**
 * Returns a list of rows with the user's liquidity pool details for given perp market index
 */
const YourPoolDetails = ({
	liquidityPool,
	fullWidth,
	openOrder,
}: {
	liquidityPool: LiquidityPool;
	fullWidth?: boolean;
	openOrder?: LPOpenOrderData;
}) => {
	if (!liquidityPool) return;

	const actions = useDriftActions();

	const settleLP = useCallback((marketIndex: number) => {
		actions.settlePnl(marketIndex);
	}, []);

	return (
		<div className="text-text-default">
			<div className="w-full pb-2">
				<DetailsRow fullWidth={fullWidth}>
					<Text.BODY3>Liquidity Provided</Text.BODY3>
					<Text.BODY3>{liquidityPool.userQuoteValue.toNotional()}</Text.BODY3>
				</DetailsRow>
				<DetailsRow fullWidth={fullWidth}>
					<Text.BODY3>BAL Shares</Text.BODY3>
					<Text.BODY3>
						{liquidityPool.userLpShares.prettyPrint(true)}
					</Text.BODY3>
				</DetailsRow>
				<DetailsRow fullWidth={fullWidth}>
					<Text.BODY3>BAL Proportion</Text.BODY3>
					<Text.BODY3>{liquidityPool.userLpProportion}</Text.BODY3>
				</DetailsRow>
				<DetailsRow fullWidth={fullWidth}>
					<Text.BODY3>In Open Orders</Text.BODY3>
					<Text.BODY3>
						{openOrder
							? `${openOrder.baseAmountLong
									.abs()
									.printShort(true)} LONG / ${openOrder.baseAmountShort
									.abs()
									.printShort(true)} SHORT`
							: `N/A`}
					</Text.BODY3>
				</DetailsRow>
			</div>
			{liquidityPool.userPerpPosition && (
				<>
					<div className="w-full pt-4 pb-2 border-t text-text-label border-container-border">
						<Text.H5>BAL Position Overview</Text.H5>
					</div>
					<div className="w-full">
						<DetailsRow fullWidth={fullWidth}>
							<Text.BODY3>Position</Text.BODY3>
							<Text.BODY3 className="inline-flex items-center">
								<MarketIcon
									className="mr-0.5"
									marketSymbol={liquidityPool.marketConfig.baseAssetSymbol}
								/>
								{`${BigNum.from(
									liquidityPool.userPerpPosition.baseSize,
									BASE_PRECISION_EXP
								).printShort()} ${liquidityPool.marketConfig.baseAssetSymbol}`}
								<span
									className={`${
										liquidityPool.userPerpPosition.direction === 'short'
											? 'text-positive-red'
											: 'text-positive-green'
									} ml-1`}
								>
									{liquidityPool.userPerpPosition.direction === 'short'
										? 'SHORT'
										: 'LONG'}
								</span>
							</Text.BODY3>
						</DetailsRow>
						<DetailsRow fullWidth={fullWidth}>
							<Text.BODY3>Notional</Text.BODY3>
							<Text.BODY3>
								{BigNum.from(
									liquidityPool.userPerpPosition.notional.abs(),
									QUOTE_PRECISION_EXP
								).toNotional()}
							</Text.BODY3>
						</DetailsRow>

						<DetailsRow fullWidth={fullWidth}>
							<Text.BODY3 className="inline-flex items-center">
								Unsettled P&L
							</Text.BODY3>
							<div className="inline-flex items-center">
								<Text.BODY3
									className={`inline-flex items-center ${
										liquidityPool.userUnsettledPnl.gtZero()
											? 'text-positive-green'
											: 'text-negative-red'
									}`}
								>
									{liquidityPool.userUnsettledPnl.toNotional()}
								</Text.BODY3>
								<Button.Secondary
									id={`settleLpFromModal_${liquidityPool.marketConfig.marketIndex}`}
									size="SMALL"
									className="ml-1"
									//disabled={liquidityPool.userUnsettledPnl.eqZero()}
									onClick={() => {
										settleLP(liquidityPool.marketConfig.marketIndex);
									}}
								>
									<Text.H5>Settle</Text.H5>
								</Button.Secondary>
							</div>
						</DetailsRow>
					</div>
				</>
			)}
		</div>
	);
};

export const YourLPDetailsMemo = memo(YourPoolDetails);
